import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Landmark, Loader2, Plus, Printer, Download, Send, ShieldCheck, CheckCircle2, Globe, FileText,
  DollarSign, Receipt, FileSignature, Eye, EyeOff, Mail,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_TOKEN = "biss_banker_token";
const LS_ID = "biss_banker_id";

const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join("; ") || fb;
  return fb;
};

const emptyForm = () => ({
  banker_name: "",
  banker_title: "Private Banker & Trust Officer",
  institution_name: "",
  institution_type: "Private Foreign Bank & Trust Institution",
  institution_address: "",
  jurisdiction: "",
  registration_no: "",
  email: "",
  phone: "",
  effective_date: "",
  powers: "",
});

const Field = ({ label, value, onChange, type = "text", area, ph, testid }) => (
  <div className="mb-3">
    <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">{label}</label>
    {area ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={ph}
        className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px] resize-y" />
    ) : (
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={ph}
        className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]" />
    )}
  </div>
);

// Verified-fax rolodex picker — fills a fax field from saved bank/servicer/IRS numbers.
const FaxPicker = ({ dir, onPick, testid }) => {
  if (!dir || dir.length === 0) return null;
  return (
    <select data-testid={testid} defaultValue=""
      onChange={(e) => { if (e.target.value) { onPick(e.target.value); e.target.value = ""; } }}
      className="bg-[#0F1A14] border border-[#1f4030] rounded-md px-2 py-1.5 text-[#9fc0af] text-[11px] max-w-[220px]">
      <option value="">📇 Pick a verified fax…</option>
      {dir.map((g) => (
        <optgroup key={g.category} label={g.category}>
          {(g.entries || []).filter((e) => e.fax).map((e) => (
            <option key={(e.id || e.label) + e.fax} value={e.fax}>{e.label} — {e.fax}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
};


const DOC_OPTIONS = [
  { id: "full", name: "Full Master Charter Package" },
  { id: "charter", name: "Master Charter" },
  { id: "association", name: "Articles of Association & By-Laws" },
  { id: "pledge", name: "Collateral Pledge" },
  { id: "treasury", name: "Treasury Authorization" },
  { id: "certificate", name: "Certificate of Authority" },
];

export default function PrivateBankerPortal() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const [token, setToken] = useState(localStorage.getItem(LS_TOKEN) || "");
  const [bankerId, setBankerId] = useState(localStorage.getItem(LS_ID) || "");
  const [me, setMe] = useState(null);
  const [f, setF] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  // eFax panel
  const [faxTo, setFaxTo] = useState("");
  const [faxCounterparty, setFaxCounterparty] = useState("");
  const [faxDoc, setFaxDoc] = useState("full");
  const [faxing, setFaxing] = useState(false);

  // Securities line of credit + instruments
  const [account, setAccount] = useState(null);
  const [creditAmt, setCreditAmt] = useState("");
  const [creditBusy, setCreditBusy] = useState(false);
  const [ins, setIns] = useState({ type: "securities_check", amount: "", payee: "", counterparty: "", counterparty_fax: "", counterparty_email: "", send_email: false, drawee: "", tenor: "at sight", memo: "", esignature_name: "", esign_attestation: false, underwriting_value: "" });
  const [insBusy, setInsBusy] = useState(false);
  const [insFax, setInsFax] = useState({});
  const [txns, setTxns] = useState([]);
  const [cashApp, setCashApp] = useState(null);
  const [showAcct, setShowAcct] = useState(false);

  const loadTxns = useCallback(async (id, tk) => {
    if (!id || !tk) return;
    try {
      const r = await axios.get(`${API}/private-bank/transactions/${id}?t=${encodeURIComponent(tk)}`);
      setTxns(r.data.transactions || []);
    } catch (e) { /* ignore */ }
  }, []);

  const money = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
  const setI = (k) => (v) => setIns((p) => ({ ...p, [k]: v }));

  const loadAccount = useCallback(async (id, tk) => {
    if (!id || !tk) return;
    try {
      const r = await axios.get(`${API}/private-bank/account/${id}?t=${encodeURIComponent(tk)}`);
      setAccount(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  const [faxDir, setFaxDir] = useState([]);
  const loadFaxDir = useCallback(async (id, tk) => {
    if (!id || !tk) return;
    try {
      const r = await axios.get(`${API}/private-bank/fax-directory/${id}?t=${encodeURIComponent(tk)}`);
      setFaxDir(r.data.directory || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (me && me.active) { loadAccount(me.id, token); loadTxns(me.id, token); loadFaxDir(me.id, token); }
    // eslint-disable-next-line
  }, [me && me.active]);

  const requestCredit = async () => {
    const amt = parseFloat(creditAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid line amount"); return; }
    setCreditBusy(true);
    try {
      const r = await axios.post(`${API}/private-bank/credit/request/${me.id}`, { t: token, amount: amt, currency: "USD" });
      setAccount(r.data);
      setCreditAmt("");
      toast.success(`Securities line approved · converted to Certificate of Deposit · ${money(amt)}`);
      loadTxns(me.id, token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setCreditBusy(false); }
  };

  const createInstrument = async () => {
    const amt = parseFloat(ins.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!ins.payee.trim()) { toast.error("Enter the payee / beneficiary"); return; }
    if (!ins.esignature_name.trim() || !ins.esign_attestation) { toast.error("Type your e-signature and check the attestation to execute"); return; }
    if (ins.counterparty.trim() && !ins.counterparty_fax.trim()) { toast.error("Enter the counterparty fax number (E.164, e.g. +13125551234) — the executed instrument is auto-faxed to them"); return; }
    if (ins.send_email && !ins.counterparty_email.trim()) { toast.error("Enter the counterparty email, or uncheck 'Also email a copy'"); return; }
    setInsBusy(true);
    try {
      const r = await axios.post(`${API}/private-bank/instrument/${me.id}`, { t: token, ...ins, amount: amt, underwriting_value: Number(ins.underwriting_value) || 0 });
      setAccount((prev) => ({ ...(prev || {}), member: r.data.member, credit_line: r.data.credit_line, certificate: r.data.certificate, instruments: r.data.instruments, instrument_types: r.data.instrument_types }));
      setIns((p) => ({ ...p, amount: "", payee: "", counterparty: "", counterparty_fax: "", counterparty_email: "", send_email: false, memo: "", underwriting_value: "", esign_attestation: false }));
      toast.success(`${r.data.instrument.type_name} created · ${r.data.instrument.number}`);
      const fx = r.data.fax;
      if (fx && fx.attempted) {
        if (fx.status === "failed") toast.error(`Auto-eFax to ${fx.to} failed: ${fx.error || "see Telnyx"}`);
        else toast.success(`Auto-eFaxed to counterparty ${fx.to} · ${fx.status || "queued"}`);
      }
      const em = r.data.email;
      if (em && em.attempted) {
        if (em.status === "failed") toast.error(`Email to ${em.to} failed: ${em.error || "see Resend"}`);
        else toast.success(`Emailed a copy to ${em.to}`);
      }
      loadTxns(me.id, token);
    } catch (e) {
      toast.error(errMsg(e, "Could not create instrument"));
    } finally { setInsBusy(false); }
  };

  const openInstrument = async (instrumentId, mode) => {
    const url = `${API}/private-bank/instrument.pdf/${me.id}?t=${encodeURIComponent(token)}&instrument_id=${instrumentId}`;
    if (mode !== "download") {
      const w = window.open(url, "_blank");
      if (!w) toast.error("Your browser blocked the document tab — allow pop-ups for this site.");
      return;
    }
    try {
      const r = await axios.get(url, { responseType: "blob" });
      const burl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = burl; a.download = `instrument-${instrumentId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(burl), 60000);
    } catch (e) { toast.error("Could not open the instrument"); }
  };

  const faxInstrument = async (instrumentId) => {
    const to = (insFax[instrumentId] || "").trim();
    if (!to) { toast.error("Enter the counterparty fax (E.164)"); return; }
    try {
      const r = await axios.post(`${API}/private-bank/instrument-efax/${me.id}`, { t: token, instrument_id: instrumentId, to });
      toast.success(`eFax ${r.data.status || "queued"} to ${r.data.to}`);
      setInsFax((p) => ({ ...p, [instrumentId]: "" }));
    } catch (e) { toast.error(errMsg(e, "eFax failed")); }
  };

  // Treasury securities (buy/sell via CUSIP reopening)
  const [tx, setTx] = useState({ action: "buy", security_type: "note", cusip: "", original_issue_cusip: "", term: "", coupon_rate: "", issue_date: "", maturity_date: "", par_amount: "", price: "100", yield_rate: "", settlement_date: "", settlement_method: "DVP", counterparty: "", counterparty_fax: "", counterparty_email: "", send_email: false, memo: "", esignature_name: "", esign_attestation: false });
  const [txBusy, setTxBusy] = useState(false);
  const [txFax, setTxFax] = useState({});
  const [txEmail, setTxEmail] = useState({});
  const setT = (k) => (v) => setTx((p) => ({ ...p, [k]: v }));

  const createTx = async () => {
    if (!(Number(tx.par_amount) > 0)) { toast.error("Enter a valid par / face amount"); return; }
    if (!tx.esignature_name.trim() || !tx.esign_attestation) { toast.error("Type your e-signature and attest to execute"); return; }
    if (tx.counterparty.trim() && !tx.counterparty_fax.trim()) { toast.error("Enter the counterparty fax number (E.164) — the executed transaction is auto-faxed to them"); return; }
    if (tx.send_email && !tx.counterparty_email.trim()) { toast.error("Enter the counterparty email, or uncheck 'Also email a copy'"); return; }
    setTxBusy(true);
    try {
      const r = await axios.post(`${API}/private-bank/treasury/${me.id}`, { t: token, ...tx, par_amount: Number(tx.par_amount), price: Number(tx.price) || 100 });
      setAccount({ member: r.data.member, credit_line: r.data.credit_line, certificate: r.data.certificate, instruments: r.data.instruments, instrument_types: r.data.instrument_types, treasury_transactions: r.data.treasury_transactions, security_types: r.data.security_types });
      setTx((p) => ({ ...p, par_amount: "", cusip: "", counterparty: "", counterparty_fax: "", counterparty_email: "", send_email: false, memo: "", esign_attestation: false }));
      toast.success(`Treasury ${r.data.transaction.action} created · ${r.data.transaction.number}`);
      const fx = r.data.fax;
      if (fx && fx.attempted) {
        if (fx.status === "failed") toast.error(`Auto-eFax to ${fx.to} failed: ${fx.error || "see Telnyx"}`);
        else toast.success(`Auto-eFaxed to counterparty ${fx.to} · ${fx.status || "queued"}`);
      }
      const em = r.data.email;
      if (em && em.attempted) {
        if (em.status === "failed") toast.error(`Email to ${em.to} failed: ${em.error || "see Resend"}`);
        else toast.success(`Emailed a copy to ${em.to}`);
      }
      loadTxns(me.id, token);
    } catch (e) {
      toast.error(errMsg(e, "Could not create transaction"));
    } finally { setTxBusy(false); }
  };

  const openTx = async (txId, mode) => {
    const url = `${API}/private-bank/treasury.pdf/${me.id}?t=${encodeURIComponent(token)}&tx_id=${txId}`;
    if (mode !== "download") {
      const w = window.open(url, "_blank");
      if (!w) toast.error("Your browser blocked the document tab — allow pop-ups for this site.");
      return;
    }
    try {
      const r = await axios.get(url, { responseType: "blob" });
      const burl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = burl; a.download = `treasury-${txId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(burl), 60000);
    } catch (e) { toast.error("Could not open the transaction"); }
  };

  const faxTx = async (txId) => {
    const to = (txFax[txId] || "").trim();
    if (!to) { toast.error("Enter the counterparty fax (E.164)"); return; }
    try {
      const r = await axios.post(`${API}/private-bank/treasury-efax/${me.id}`, { t: token, tx_id: txId, to });
      toast.success(`eFax ${r.data.status || "queued"} to ${r.data.to}`);
      setTxFax((p) => ({ ...p, [txId]: "" }));
    } catch (e) { toast.error(errMsg(e, "eFax failed")); }
  };

  const emailTx = async (txId) => {
    const to = (txEmail[txId] || "").trim();
    if (!to || !to.includes("@")) { toast.error("Enter the recipient email address"); return; }
    try {
      const r = await axios.post(`${API}/private-bank/treasury-email/${me.id}`, { t: token, tx_id: txId, to });
      toast.success(`Emailed to ${r.data.to}`);
      setTxEmail((p) => ({ ...p, [txId]: "" }));
    } catch (e) { toast.error(errMsg(e, "Email failed")); }
  };

  // Treasury auction desk feed + holdings movement
  const [auctions, setAuctions] = useState(null);
  const [auctionsLoading, setAuctionsLoading] = useState(false);
  const [autoApplied, setAutoApplied] = useState(false);
  const loadAuctions = async (auto = false) => {
    setAuctionsLoading(true);
    try {
      const r = await axios.get(`${API}/private-bank/treasury/auctions/${me.id}?t=${encodeURIComponent(token)}&reopening_only=true`);
      setAuctions(r.data);
      if (r.data.error && !auto) toast.error(r.data.error);
      // Auto-populate the ticket with the most recent reopening CUSIP + its security data
      if (auto && !autoApplied && r.data?.auctions?.length) {
        const a = r.data.auctions[0];
        const todayISO = new Date().toISOString().slice(0, 10);
        setTx((p) => (p.cusip ? p : {
          ...p,
          security_type: a.security_type_id || "note",
          cusip: a.cusip || "",
          original_issue_cusip: a.original_cusip || "",
          term: a.security_term || "",
          coupon_rate: a.interest_rate ? `${a.interest_rate}%` : "",
          yield_rate: a.high_yield ? `${a.high_yield}%` : "",
          price: a.price_per100 ? String(a.price_per100) : (p.price || "100"),
          issue_date: a.issue_date || "",
          reopening_date: a.auction_date || todayISO,
          maturity_date: a.maturity_date || "",
          settlement_date: a.issue_date || todayISO,
        }));
        setAutoApplied(true);
      }
    } catch (e) { if (!auto) toast.error("Treasury desk feed unavailable"); } finally { setAuctionsLoading(false); }
  };
  // On an active account, fetch live reopening CUSIPs once and pre-fill the ticket.
  useEffect(() => {
    if (account && me && me.active && !auctions) loadAuctions(true);
    // eslint-disable-next-line
  }, [account && !!account.member, me && me.active]);
  const applyAuction = (a) => {
    const todayISO = new Date().toISOString().slice(0, 10);
    setTx((p) => ({
      ...p,
      security_type: a.security_type_id || "note",
      cusip: a.cusip || "",
      original_issue_cusip: a.original_cusip || "",
      term: a.security_term || "",
      coupon_rate: a.interest_rate ? `${a.interest_rate}%` : "",
      yield_rate: a.high_yield ? `${a.high_yield}%` : "",
      price: a.price_per100 ? String(a.price_per100) : (p.price || "100"),
      issue_date: a.issue_date || "",
      reopening_date: a.auction_date || todayISO,
      maturity_date: a.maturity_date || "",
      settlement_date: a.issue_date || todayISO,
    }));
    toast.success(`Loaded CUSIP ${a.cusip} into the ticket`);
  };
  const [moveFor, setMoveFor] = useState(null);
  const [moveForm, setMoveForm] = useState({ mode: "transfer", third_party: "", third_party_address: "", collateral_pct: "102", rebate_rate: "", term: "Open", memo: "", esignature_name: "", esign_attestation: false });
  const setMv = (k) => (v) => setMoveForm((p) => ({ ...p, [k]: v }));
  const openMoveFor = (txId, mode) => { setMoveFor(txId); setMoveForm((p) => ({ ...p, mode })); };
  const submitMove = async () => {
    if (!moveForm.third_party.trim()) { toast.error("Enter the third party (assignee / borrower)"); return; }
    if (!moveForm.esignature_name.trim() || !moveForm.esign_attestation) { toast.error("Type your e-signature and attest"); return; }
    try {
      const r = await axios.post(`${API}/private-bank/treasury/${me.id}/move`, { t: token, tx_id: moveFor, ...moveForm, collateral_pct: Number(moveForm.collateral_pct) || 102 });
      setAccount((prev) => ({ ...prev, treasury_transactions: r.data.treasury_transactions }));
      toast.success(`${moveForm.mode === "loan" ? "Securities loan" : "Transfer"} executed · ${r.data.movement.number}`);
      setMoveFor(null);
      setMoveForm((p) => ({ ...p, third_party: "", third_party_address: "", memo: "", esign_attestation: false }));
    } catch (e) { toast.error(errMsg(e, "Movement failed")); }
  };
  const openReceipt = (txId) => window.open(`${API}/private-bank/treasury/receipt.pdf/${me.id}?t=${encodeURIComponent(token)}&tx_id=${txId}`, "_blank");
  const openMovementDoc = (txId, mvId) => window.open(`${API}/private-bank/treasury/movement.pdf/${me.id}?t=${encodeURIComponent(token)}&tx_id=${txId}&movement_id=${mvId}`, "_blank");

  // BISS $30B Treasury Securities Reserve — securities loans drawn by the banker
  const [reserve, setReserve] = useState(null);
  const [rsl, setRsl] = useState({ purchase_price: "", security_type: "note", cusip: "", memo: "", esignature_name: "", esign_attestation: false });
  const [rslBusy, setRslBusy] = useState(false);
  const [rslFax, setRslFax] = useState({});
  const [rslEmail, setRslEmail] = useState({});
  const setR = (k) => (v) => setRsl((p) => ({ ...p, [k]: v }));
  const loadReserve = useCallback(async (id, tk) => {
    if (!id || !tk) return;
    try {
      const r = await axios.get(`${API}/private-bank/treasury/reserve/${id}?t=${encodeURIComponent(tk)}`);
      setReserve(r.data);
    } catch (e) { /* ignore */ }
  }, []);
  const createReserveLoan = async () => {
    if (!(Number(rsl.purchase_price) > 0)) { toast.error("Enter a valid total purchase price"); return; }
    if (!rsl.esignature_name.trim() || !rsl.esign_attestation) { toast.error("Type your e-signature and attest to execute"); return; }
    setRslBusy(true);
    try {
      const r = await axios.post(`${API}/private-bank/treasury/reserve/${me.id}`, { t: token, ...rsl, purchase_price: Number(rsl.purchase_price) });
      setReserve(r.data.reserve);
      setRsl((p) => ({ ...p, purchase_price: "", cusip: "", memo: "", esign_attestation: false }));
      toast.success(`Securities loan executed · ${r.data.loan.number} · fee ${money(r.data.loan.fee_amount)}`);
    } catch (e) { toast.error(errMsg(e, "Could not execute the securities loan")); } finally { setRslBusy(false); }
  };
  const renewReserve = async (loanId) => {
    try {
      const r = await axios.post(`${API}/private-bank/treasury/reserve/${me.id}/renew/${loanId}`, { t: token });
      setReserve(r.data.reserve); toast.success("Loan renewed for a further 90-day term (new 0.5% fee applies)");
    } catch (e) { toast.error(errMsg(e, "Renew failed")); }
  };
  const returnReserve = async (loanId) => {
    try {
      const r = await axios.post(`${API}/private-bank/treasury/reserve/${me.id}/return/${loanId}`, { t: token });
      setReserve(r.data.reserve); toast.success("Securities returned to the reserve");
    } catch (e) { toast.error(errMsg(e, "Return failed")); }
  };
  const settleReserveCredit = async (loanId) => {
    try {
      const r = await axios.post(`${API}/private-bank/treasury/reserve/${me.id}/settle-credit/${loanId}`, { t: token });
      setReserve(r.data.reserve);
      if (r.data.credit_line) setAccount((prev) => ({ ...prev, credit_line: r.data.credit_line }));
      toast.success(`Fee ${money(r.data.settled)} deducted from your Securities Line of Credit`);
    } catch (e) { toast.error(errMsg(e, "Could not deduct from credit line")); }
  };
  const payReserveStripe = async (loanId) => {
    try {
      const r = await axios.post(`${API}/private-bank/treasury/reserve/${me.id}/pay/${loanId}`, { t: token, origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { toast.error(errMsg(e, "Could not start payment")); }
  };
  const openReserveAgreement = (loanId) => window.open(`${API}/private-bank/treasury/reserve/agreement.pdf/${me.id}?t=${encodeURIComponent(token)}&loan_id=${loanId}`, "_blank");
  const faxReserve = async (loanId) => {
    const to = (rslFax[loanId] || "").trim();
    if (!to) { toast.error("Enter the counterparty fax (E.164)"); return; }
    try {
      const r = await axios.post(`${API}/private-bank/treasury/reserve-efax/${me.id}`, { t: token, loan_id: loanId, to });
      toast.success(`eFax ${r.data.status || "queued"} to ${r.data.to}`); setRslFax((p) => ({ ...p, [loanId]: "" }));
    } catch (e) { toast.error(errMsg(e, "eFax failed")); }
  };
  const emailReserve = async (loanId) => {
    const to = (rslEmail[loanId] || "").trim();
    if (!to || !to.includes("@")) { toast.error("Enter the recipient email address"); return; }
    try {
      const r = await axios.post(`${API}/private-bank/treasury/reserve-email/${me.id}`, { t: token, loan_id: loanId, to });
      toast.success(`Emailed to ${r.data.to}`); setRslEmail((p) => ({ ...p, [loanId]: "" }));
    } catch (e) { toast.error(errMsg(e, "Email failed")); }
  };
  useEffect(() => {
    if (account && me && me.active && !reserve) loadReserve(me.id, token);
    // eslint-disable-next-line
  }, [account && !!account.member, me && me.active]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const loadMe = useCallback(async (id, tk) => {
    if (!id || !tk) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/private-bank/me/${id}?t=${encodeURIComponent(tk)}`);
      setMe(r.data);
    } catch (e) {
      // token invalid → reset to registration
      localStorage.removeItem(LS_TOKEN);
      localStorage.removeItem(LS_ID);
      setToken(""); setBankerId(""); setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle Stripe return + initial load
  useEffect(() => {
    const retBanker = params.get("banker");
    const session = params.get("session");
    const cancelled = params.get("cancelled");
    if (cancelled) { toast.info("Subscription was cancelled."); setParams({}, { replace: true }); }
    const id = retBanker || bankerId;
    const tk = token;
    const rslSession = params.get("rsl_session");
    const rslLoan = params.get("rsl_loan");
    if (rslSession && rslLoan && id && tk) {
      (async () => {
        try {
          const r = await axios.get(`${API}/private-bank/treasury/reserve/${id}/pay-status/${rslLoan}?t=${encodeURIComponent(tk)}&session_id=${rslSession}`);
          if (r.data.reserve) setReserve(r.data.reserve);
          if (r.data.payment_status === "paid") toast.success("Securities loan fee paid.");
        } catch (e) { /* ignore */ } finally {
          loadMe(id, tk); setParams({}, { replace: true });
        }
      })();
      return;
    }
    if (session && id && tk) {
      (async () => {
        setLoading(true);
        try {
          const r = await axios.get(`${API}/private-bank/subscribe-status/${id}?session_id=${session}&t=${encodeURIComponent(tk)}`);
          setMe(r.data);
          if (r.data.active) toast.success("Membership active — your charter is unlocked.");
        } catch (e) { /* ignore */ } finally {
          setLoading(false);
          setParams({}, { replace: true });
        }
      })();
    } else if (id && tk) {
      loadMe(id, tk);
    }
    // eslint-disable-next-line
  }, []);

  const register = async () => {
    if (!f.banker_name.trim() || !f.institution_name.trim()) {
      toast.error("Banker name and institution name are required");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/private-bank/register`, f);
      localStorage.setItem(LS_TOKEN, r.data.token);
      localStorage.setItem(LS_ID, r.data.id);
      setToken(r.data.token);
      setBankerId(r.data.id);
      setMe(r.data);
      toast.success(`Registered · Charter No. ${r.data.charter_no}`);
    } catch (e) {
      toast.error(errMsg(e, "Registration failed"));
    } finally {
      setBusy(false);
    }
  };

  const [login, setLogin] = useState({ email: "", access_code: "" });
  const [loginBusy, setLoginBusy] = useState(false);
  const doLogin = async () => {
    if (!login.email.trim() || !login.access_code.trim()) { toast.error("Enter your email and access code"); return; }
    setLoginBusy(true);
    try {
      const r = await axios.post(`${API}/private-bank/login`, login);
      localStorage.setItem(LS_TOKEN, r.data.token);
      localStorage.setItem(LS_ID, r.data.id);
      setToken(r.data.token); setBankerId(r.data.id); setMe(r.data);
      toast.success(`Welcome back, ${r.data.banker_name}`);
    } catch (e) {
      toast.error(errMsg(e, "Login failed"));
    } finally { setLoginBusy(false); }
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/private-bank/subscribe/${bankerId || me.id}`, {
        origin_url: window.location.origin, t: token,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start subscription"));
      setBusy(false);
    }
  };

  const payCashApp = async () => {
    try {
      const r = await axios.post(`${API}/private-bank/cashapp-intent/${bankerId || me.id}`, { t: token });
      setCashApp(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Could not start Cash App payment"));
    }
  };

  const openAccountDoc = (kind) => {
    const path = kind === "certificate" ? "certificate.pdf" : kind === "statement" ? "cd-statement.pdf" : "credit-agreement.pdf";
    // Open the server URL directly in the click gesture — reliable across browsers
    // (opening about:blank then assigning a blob URL left the tab blank in Chrome/Safari).
    const w = window.open(`${API}/private-bank/${path}/${me.id}?t=${encodeURIComponent(token)}`, "_blank");
    if (!w) toast.error("Your browser blocked the document tab — allow pop-ups for this site and try again.");
  };

  const [stmtEmail, setStmtEmail] = useState("");
  const [stmtBusy, setStmtBusy] = useState(false);
  const emailStatement = async () => {
    setStmtBusy(true);
    try {
      const r = await axios.post(`${API}/private-bank/cd-statement-email/${me.id}`, { t: token, to: stmtEmail.trim() });
      toast.success(`CD statement emailed to ${r.data.to}`);
      setStmtEmail("");
    } catch (e) {
      toast.error(errMsg(e, "Could not email the statement"));
    } finally { setStmtBusy(false); }
  };

  const repayInstrument = async (instrumentId) => {
    try {
      const r = await axios.post(`${API}/private-bank/repay/${me.id}`, { t: token, instrument_id: instrumentId });
      setAccount(r.data);
      toast.success("Instrument repaid — amount credited back to your Certificate of Deposit");
      loadTxns(me.id, token);
    } catch (e) { toast.error(errMsg(e, "Repayment failed")); }
  };

  const openDoc = async (doc, mode) => {
    const url = `${API}/private-bank/package.pdf/${me.id}?t=${encodeURIComponent(token)}&doc=${doc}`;
    if (mode !== "download") {
      const w = window.open(url, "_blank");
      if (!w) toast.error("Your browser blocked the document tab — allow pop-ups for this site.");
      return;
    }
    try {
      const r = await axios.get(url, { responseType: "blob" });
      const burl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = burl;
      a.download = `master-charter-${(me.institution_name || "member").replace(/\s+/g, "_")}-${doc}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(burl), 60000);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Activate your $499/month membership first.");
      else toast.error("Could not generate the document");
    }
  };

  const sendFax = async () => {
    if (!faxTo.trim()) { toast.error("Enter the counterparty fax number (E.164, e.g. +13125551234)"); return; }
    setFaxing(true);
    try {
      const r = await axios.post(`${API}/private-bank/efax/${me.id}`, {
        to: faxTo.trim(), t: token, doc: faxDoc, counterparty: faxCounterparty.trim(),
      });
      toast.success(`eFax ${r.data.status || "queued"} to ${r.data.to}`);
      setFaxTo(""); setFaxCounterparty("");
    } catch (e) {
      toast.error(errMsg(e, "eFax failed"));
    } finally {
      setFaxing(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(LS_TOKEN); localStorage.removeItem(LS_ID);
    setToken(""); setBankerId(""); setMe(null); setF(emptyForm());
  };

  return (
    <div className="min-h-screen bg-[#07120D] text-[#E7F3EE]" data-testid="biss-portal-page">
      <header className="px-6 sm:px-10 py-4 flex items-center justify-between border-b border-[#143026] bg-[#050D09]">
        <div className="flex items-center gap-3">
          <Landmark className="text-[#E4C97A]" size={20} />
          <div>
            <div className="text-[9px] tracking-[0.24em] uppercase text-[#E4C97A] font-bold">Foreign Banking Association · International Law</div>
            <h1 className="text-lg font-serif">Bank of International Settlements and Securities</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {me ? (
            <button onClick={logout} data-testid="biss-logout-btn"
              className="text-[11px] uppercase tracking-[0.12em] text-[#7fa08f] hover:text-[#E7F3EE] px-3 py-2">New registration</button>
          ) : (
            <button onClick={() => { const el = document.getElementById("biss-login"); if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); } setTimeout(() => { const em = document.querySelector('[data-testid="biss-login-email"]'); if (em) em.focus(); }, 400); }} data-testid="biss-header-login-btn"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-4 py-2 rounded-full transition-colors">
              <ShieldCheck size={13} /> Log In
            </button>
          )}
          <button onClick={() => nav("/")} data-testid="biss-home-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#E7F3EE] border border-[#1f4030] hover:border-[#E4C97A] px-4 py-2 rounded-full transition-colors">
            <ArrowLeft size={13} /> Home
          </button>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-4 sm:px-6 py-8 space-y-8">
        {loading && (
          <div className="flex items-center gap-2 text-[#7fa08f] text-sm"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        )}

        {!me ? (
          <>
            <section className="text-center max-w-[720px] mx-auto">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#8a7530] mb-3">
                <Globe size={13} className="text-[#E4C97A]" /> Private Foreign Banking Association
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-[#F4ECCF]">
                Charter your institution as a member foreign bank
              </h2>
              <p className="text-[13px] text-[#9fc0af] mt-4 leading-relaxed">
                Register your institution and private banker to receive a private Master Charter evidencing your
                membership in this private association — your private membership record and notice of participation (this
                is <b>not</b> a government or banking license and confers no government authority) — then print, download,
                and eFax your back-office instruments to any counterparty. Registration is a one-time <b className="text-[#E4C97A]">$3,499</b> (includes
                your first month), then <b className="text-[#E4C97A]">$499/month</b>.
              </p>
            </section>

            <section className="bg-gradient-to-b from-[#0C1812] to-[#091410] border border-[#143026] rounded-2xl p-7" data-testid="biss-benefits-cover">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#8a7530] mb-2">
                  <Landmark size={13} className="text-[#E4C97A]" /> What a Private Banker Can Do
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#F4ECCF]">Your Private Bank Charter — Capabilities &amp; Benefits</h3>
                <p className="text-[12.5px] text-[#9fc0af] mt-2 max-w-[640px] mx-auto">
                  On activation, your institution receives a Securities Loan Line of Credit that is converted into a
                  Certificate of Deposit held with the Association — the extended credit you disburse as securities checks,
                  drafts, and Treasuries, all ledgered in the Association's Master Reserve Account.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: FileSignature, t: "Securities Line of Credit → CD", d: "Request a line; it is documented by a Securities Loan Line of Credit Agreement and converted to a Certificate of Deposit in your account." },
                  { icon: FileText, t: "Issue Private Instruments", d: "Draw your CD by issuing securities checks, sight drafts (barter exchange under the IRC), bills of exchange, and letters of credit." },
                  { icon: Landmark, t: "Treasury Desk", d: "Buy and sell U.S. Treasury securities by CUSIP reopening directly with the BISS desk, auto-populated from live TreasuryDirect data." },
                  { icon: Globe, t: "Reserve Securities Loans", d: "Draw 90-day securities loans from the $30B BISS Treasury Securities Reserve at a 0.5% fee per term." },
                  { icon: Send, t: "Auto-eFax to Counterparties", d: "Every transaction executed to a counterparty is automatically eFaxed to them on execution — nothing is missed." },
                  { icon: Receipt, t: "Full Transaction Record", d: "Every instrument, trade, transfer, loan, and eFax is recorded in your transaction database and mirrored to the Association." },
                ].map((b) => (
                  <div key={b.t} className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                    <b.icon size={18} className="text-[#6FD3AE]" />
                    <div className="text-[13px] font-semibold text-[#F4ECCF] mt-2">{b.t}</div>
                    <div className="text-[11.5px] text-[#9fc0af] mt-1 leading-relaxed">{b.d}</div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-6 text-[12px] text-[#7fa08f]">
                Pay once — <b className="text-[#E4C97A]">$3,499</b> (includes your first month) via <b>Stripe</b> or <b className="text-[#00D632]">Cash App {`$seutrade`}</b> — then <b className="text-[#E4C97A]">$499/month</b>.
              </div>
            </section>

            <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-register-card">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={16} className="text-[#E4C97A]" />
                <h3 className="font-serif text-[18px]">Register with the Bank of International Settlements and Securities — Private Banker Registration</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6">
                <Field label="Private Banker — Full Name" value={f.banker_name} onChange={set("banker_name")} ph="e.g. Jonathan P. Reed" testid="biss-banker-name" />
                <Field label="Banker Title" value={f.banker_title} onChange={set("banker_title")} testid="biss-banker-title" />
                <Field label="Institution Name" value={f.institution_name} onChange={set("institution_name")} ph="e.g. Meridian Private Trust & Bank" testid="biss-institution-name" />
                <Field label="Institution Type" value={f.institution_type} onChange={set("institution_type")} testid="biss-institution-type" />
                <Field label="Principal Office Address" value={f.institution_address} onChange={set("institution_address")} ph="Street, City, State/Region, Country" testid="biss-institution-address" />
                <Field label="Governing Jurisdiction" value={f.jurisdiction} onChange={set("jurisdiction")} ph="e.g. Delaware, United States" testid="biss-jurisdiction" />
                <Field label="Registration / EIN No." value={f.registration_no} onChange={set("registration_no")} testid="biss-registration-no" />
                <Field label="Effective Date" value={f.effective_date} onChange={set("effective_date")} type="date" testid="biss-effective-date" />
                <Field label="Email" value={f.email} onChange={set("email")} type="email" testid="biss-email" />
                <Field label="Phone" value={f.phone} onChange={set("phone")} testid="biss-phone" />
              </div>
              <Field label="Additional Authorized Powers (optional)" value={f.powers} onChange={set("powers")} area
                ph="Any extra chartered powers to recite in the Master Charter" testid="biss-powers" />
              <button onClick={register} disabled={busy} data-testid="biss-register-btn"
                className="inline-flex items-center gap-2 bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-colors mt-2">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Register & Reserve Charter
              </button>
            </section>

            <section id="biss-login" className="bg-[#0C1812] border-2 border-[#1f5540] rounded-2xl p-6" data-testid="biss-login-card">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={16} className="text-[#6FD3AE]" />
                <h3 className="font-serif text-[18px] text-[#F4ECCF]">Already registered? Log in to your account</h3>
              </div>
              <p className="text-[12px] text-[#9fc0af] mb-4">
                Use your account email and your access code (your Membership, Charter, or Certificate number, e.g. <b className="text-[#E4C97A]">BISS-M-XXXXXX</b>).
                Once logged in and active, you can request your securities line of credit, open your Certificate of Deposit and statements, and view your Treasury Prime routing &amp; account numbers.
              </p>
              <div className="grid sm:grid-cols-2 gap-x-6">
                <Field label="Account Email" value={login.email} onChange={(v) => setLogin((p) => ({ ...p, email: v }))} type="email" testid="biss-login-email" />
                <Field label="Access Code (Membership / Charter / Certificate No.)" value={login.access_code} onChange={(v) => setLogin((p) => ({ ...p, access_code: v }))} ph="e.g. BISS-M-XXXXXX" testid="biss-login-code" />
                <div>
                  <button onClick={doLogin} disabled={loginBusy} data-testid="biss-login-btn"
                    className="inline-flex items-center gap-2 bg-[#6FD3AE] hover:bg-[#54c097] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-colors">
                    {loginBusy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Log In
                  </button>
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            {/* Member summary */}
            <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-member-card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-serif text-[20px] text-[#F4ECCF]">{me.institution_name}</div>
                  <div className="text-[12px] text-[#9fc0af]">{me.banker_name} · {me.banker_title}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-[#7fa08f]">
                    <span>Membership: <b className="text-[#cfe3d8]">{me.membership_no}</b></span>
                    <span>Charter: <b className="text-[#cfe3d8]">{me.charter_no}</b></span>
                    <span>Cert: <b className="text-[#cfe3d8]">{me.certificate_no}</b></span>
                  </div>
                </div>
                <div className="text-right">
                  {me.active ? (
                    <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#06160F] bg-[#6FD3AE] px-3 py-1.5 rounded-full" data-testid="biss-status-active">
                      <CheckCircle2 size={14} /> Membership active
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#E4C97A] border border-[#8A6D1F] px-3 py-1.5 rounded-full" data-testid="biss-status-inactive">
                      Inactive · $3,499 to register
                    </div>
                  )}
                  {me.current_period_end && (
                    <div className="text-[10.5px] text-[#7fa08f] mt-1">Renews {new Date(me.current_period_end).toLocaleDateString()}</div>
                  )}
                </div>
              </div>
            </section>

            {!me.active ? (
              <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6 text-center" data-testid="biss-subscribe-card">
                <h3 className="font-serif text-[18px] text-[#F4ECCF]">Complete registration — $3,499 one-time</h3>
                <p className="text-[12.5px] text-[#9fc0af] mt-2 max-w-[600px] mx-auto">
                  Your charter numbers are reserved. Pay the one-time <b>$3,499</b> registration to activate your account
                  (covers registration, your first month's subscription, account transaction, financing statement, and
                  transfer agent fees). After that, only <b>$499/month</b> recurs.
                </p>
                <ul className="text-[11.5px] text-[#7fa08f] mt-3 max-w-[420px] mx-auto text-left space-y-1" data-testid="biss-fee-schedule">
                  <li className="flex justify-between"><span>Registration Fee</span><span className="text-[#cfe3d8]">$2,350.00</span></li>
                  <li className="flex justify-between"><span>Monthly Subscription (first month)</span><span className="text-[#cfe3d8]">$499.00</span></li>
                  <li className="flex justify-between"><span>Account Transaction Fee</span><span className="text-[#cfe3d8]">$300.00</span></li>
                  <li className="flex justify-between"><span>Financing Statement Registration Fee</span><span className="text-[#cfe3d8]">$250.00</span></li>
                  <li className="flex justify-between"><span>Transfer Agent Processing Fee</span><span className="text-[#cfe3d8]">$100.00</span></li>
                  <li className="flex justify-between border-t border-[#143026] pt-1 mt-1 font-bold text-[#E4C97A]"><span>Total due today</span><span>$3,499.00</span></li>
                </ul>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
                  <button onClick={subscribe} disabled={busy} data-testid="biss-subscribe-btn"
                    className="inline-flex items-center gap-2 bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-6 py-3 rounded-lg transition-colors">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Pay $3,499 with Stripe
                  </button>
                  <button onClick={payCashApp} data-testid="biss-cashapp-btn"
                    className="inline-flex items-center gap-2 bg-[#00D632] hover:bg-[#00c02d] text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-6 py-3 rounded-lg transition-colors">
                    <DollarSign size={14} /> Pay with Cash App
                  </button>
                </div>
                {cashApp && (
                  <div className="mt-5 max-w-[560px] mx-auto bg-[#071A0D] border border-[#00D632]/40 rounded-xl p-4 text-left" data-testid="biss-cashapp-instructions">
                    <div className="text-[13px] font-bold text-[#00D632] flex items-center gap-2"><DollarSign size={15} /> Cash App Payment Instructions</div>
                    <p className="text-[12.5px] text-[#cfe3d8] mt-2 leading-relaxed">
                      Send <b>${Number(cashApp.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</b> to
                      <b className="text-[#00D632]"> {cashApp.handle}</b> on Cash App. In the payment note, include your
                      full name exactly as registered — <b>{cashApp.full_name}</b>. Your account will be activated
                      <b> within 15 minutes</b> of payment. After the first month, membership continues at
                      <b> ${Number(cashApp.monthly_fee).toLocaleString("en-US", { minimumFractionDigits: 2 })}/month</b>.
                    </p>
                  </div>
                )}
              </section>
            ) : (
              <>
                {/* Securities Line of Credit */}
                <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-credit-card">
                  <h3 className="font-serif text-[18px] text-[#F4ECCF] mb-1">Securities Line of Credit</h3>
                  {(!account || !account.credit_line || account.credit_line.status !== "approved") ? (
                    <>
                      <p className="text-[12.5px] text-[#9fc0af] mb-4">
                        Request a securities line of credit from the Master Association. Once approved it appears as your
                        available balance, drawable via letters of credit, bills of exchange, sight drafts, and securities checks.
                      </p>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">Line Amount (USD)</label>
                          <input data-testid="biss-credit-amount" type="number" value={creditAmt} onChange={(e) => setCreditAmt(e.target.value)} placeholder="e.g. 5000000"
                            className="w-56 bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]" />
                        </div>
                        <button onClick={requestCredit} disabled={creditBusy} data-testid="biss-credit-request-btn"
                          className="inline-flex items-center gap-2 bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-colors">
                          {creditBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Request Line
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-3 gap-3 mt-3" data-testid="biss-credit-balance">
                      <div className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#7fa08f]">Approved Line</div>
                        <div className="font-serif text-[20px] text-[#F4ECCF] mt-1">{money(account.credit_line.approved_amount)}</div>
                      </div>
                      <div className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#7fa08f]">Used</div>
                        <div className="font-serif text-[20px] text-[#E4C97A] mt-1">{money(account.credit_line.used_amount)}</div>
                      </div>
                      <div className="bg-[#0F1A14] border border-[#6FD3AE]/40 rounded-xl p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#7fa08f]">Available Balance</div>
                        <div className="font-serif text-[20px] text-[#6FD3AE] mt-1" data-testid="biss-available-balance">{money(account.credit_line.available_amount)}</div>
                      </div>
                      <div className="col-span-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-[#7fa08f]">
                        <span>Facility {account.credit_line.facility_no}{account.credit_line.cd_no ? ` · Certificate of Deposit ${account.credit_line.cd_no}` : ""}</span>
                        <span className="flex gap-2">
                          <button onClick={() => openAccountDoc("agreement")} data-testid="biss-sloc-agreement-btn"
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#E7F3EE] border border-[#1f4030] hover:border-[#6FD3AE] px-2.5 py-1.5 rounded-md"><FileSignature size={11} /> SLOC Agreement</button>
                          <button onClick={() => openAccountDoc("certificate")} data-testid="biss-cd-btn"
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-2.5 py-1.5 rounded-md"><FileText size={11} /> Certificate of Deposit</button>
                          <button onClick={() => openAccountDoc("statement")} data-testid="biss-cd-statement-btn"
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#E7F3EE] border border-[#1f4030] hover:border-[#6FD3AE] px-2.5 py-1.5 rounded-md"><Receipt size={11} /> CD Statement</button>
                        </span>
                      </div>
                      {account.credit_line.cd_no && (
                        <div className="col-span-3 flex flex-wrap items-center gap-2 text-[11px] text-[#7fa08f] bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2" data-testid="biss-cd-statement-email-row">
                          <span className="text-[10px] uppercase tracking-[0.12em] text-[#6f8f7e]">Email CD statement</span>
                          <input value={stmtEmail} onChange={(e) => setStmtEmail(e.target.value)} type="email"
                            placeholder={(account.member && account.member.email) || "you@example.com or your accountant"}
                            data-testid="biss-cd-statement-email-input"
                            className="flex-1 min-w-[200px] max-w-sm bg-[#0C1812] border border-[#1f4030] rounded-md px-3 py-1.5 text-[#E7F3EE] text-[12px]" />
                          <button onClick={emailStatement} disabled={stmtBusy} data-testid="biss-cd-statement-email-btn"
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 px-3 py-1.5 rounded-md">
                            {stmtBusy ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Email Statement</button>
                          <span className="text-[10px] text-[#6f8f7e]">Leave blank to send to your email on file · a copy goes to the firm</span>
                        </div>
                      )}
                      {account.member && account.member.bank_account && account.member.bank_account.account_number && (
                        <div className="col-span-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] text-[#9fc0af] bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2" data-testid="biss-bank-account">
                          <span className="text-[10px] uppercase tracking-[0.12em] text-[#6f8f7e]">Bank Account (Treasury Prime)</span>
                          <span>Account <b className="text-[#E7F3EE] font-mono">{showAcct ? account.member.bank_account.account_number : "••••" + String(account.member.bank_account.account_number).slice(-4)}</b></span>
                          <button onClick={() => setShowAcct((v) => !v)} data-testid="biss-acct-toggle" className="text-[#6f8f7e] hover:text-[#E4C97A]" title={showAcct ? "Hide" : "Reveal"}>{showAcct ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                          <span>Routing <b className="text-[#E7F3EE] font-mono">{account.member.bank_account.routing_number}</b></span>
                          <span className="text-[10px] text-[#6f8f7e]">Printed on your instruments</span>
                        </div>
                      )}
                    </div>
                  )}
                </section>

                {/* Issue instruments */}
                {account && account.credit_line && account.credit_line.status === "approved" && (
                  <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-instruments-card">
                    <h3 className="font-serif text-[18px] text-[#F4ECCF] mb-1">Transfer · Assign · Pledge — Issue an Instrument</h3>
                    <p className="text-[12px] text-[#7fa08f] mb-4">Draw against your available balance. Check/draft instruments print a 6.6" check with a detachable stub.</p>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">Instrument Type</label>
                        <select value={ins.type} onChange={(e) => setI("type")(e.target.value)} data-testid="biss-ins-type"
                          className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]">
                          {(account.instrument_types || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      <Field label="Amount (USD)" value={ins.amount} onChange={setI("amount")} type="number" ph="e.g. 250000" testid="biss-ins-amount" />
                      <Field label="Pay to the Order of / Beneficiary" value={ins.payee} onChange={setI("payee")} ph="Payee or beneficiary name" testid="biss-ins-payee" />
                      <Field label="Counterparty (optional)" value={ins.counterparty} onChange={setI("counterparty")} testid="biss-ins-counterparty" />
                      <div>
                        <Field label={ins.counterparty.trim() ? "Counterparty Fax (REQUIRED — auto-eFaxed on execution)" : "Counterparty Fax (E.164)"} value={ins.counterparty_fax} onChange={setI("counterparty_fax")} ph="+13125551234" testid="biss-ins-counterparty-fax" />
                        <div className="-mt-2 mb-3"><FaxPicker dir={faxDir} testid="biss-ins-fax-picker" onPick={(fax) => setI("counterparty_fax")(fax)} /></div>
                      </div>
                      <Field label="Counterparty Email (optional)" value={ins.counterparty_email} onChange={setI("counterparty_email")} ph="counterparty@example.com" testid="biss-ins-counterparty-email" />
                      <Field label="Drawee / Drawn On (optional)" value={ins.drawee} onChange={setI("drawee")} ph="Defaults to the Association" testid="biss-ins-drawee" />
                      <Field label="Tenor" value={ins.tenor} onChange={setI("tenor")} ph="at sight / 30 days" testid="biss-ins-tenor" />
                    </div>
                    {ins.counterparty.trim() && (
                      <label className="flex items-center gap-2 text-[12px] text-[#9fc0af] mt-2 mb-1 cursor-pointer" data-testid="biss-ins-send-email-row">
                        <input type="checkbox" checked={ins.send_email} onChange={(e) => setI("send_email")(e.target.checked)} data-testid="biss-ins-send-email" />
                        <span className="inline-flex items-center gap-1"><Mail size={12} /> Also email a copy of the executed instrument to the counterparty (sent alongside the auto-eFax; the firm is copied)</span>
                      </label>
                    )}
                    <Field label="Memo (optional)" value={ins.memo} onChange={setI("memo")} testid="biss-ins-memo" />
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 border-t border-[#143026] pt-4 mt-1">
                      <Field label="Underwriting Service Award (USD — defaults to amount, never less)" value={ins.underwriting_value} onChange={setI("underwriting_value")} type="number" ph="defaults to the tendered amount" testid="biss-ins-underwriting" />
                      <Field label="Your E-Signature (type full name)" value={ins.esignature_name} onChange={setI("esignature_name")} ph="Type your full legal name" testid="biss-ins-esign-name" />
                    </div>
                    {ins.esignature_name.trim() && (
                      <div className="mb-2" data-testid="biss-ins-esign-preview">
                        <span style={{ fontFamily: "'Great Vibes', cursive", fontSize: 26, color: "#6FD3AE" }}>{ins.esignature_name}</span>
                      </div>
                    )}
                    <label className="flex items-start gap-2 text-[12px] text-[#9fc0af] mb-3 cursor-pointer">
                      <input type="checkbox" checked={ins.esign_attestation} onChange={(e) => setI("esign_attestation")(e.target.checked)} data-testid="biss-ins-esign-attest" className="mt-0.5" />
                      <span>I adopt the typed name above as my electronic signature (E-SIGN/UETA) and execute this instrument and its companion <b>UCC Article 9 Security Agreement &amp; Underwriting Service Award</b>, granting the counterparty a first-priority perfected security interest and acknowledging the barter cross-grant and underwriting award (not less than the tendered value).</span>
                    </label>
                    <button onClick={createInstrument} disabled={insBusy} data-testid="biss-ins-create-btn"
                      className="inline-flex items-center gap-2 bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-colors mt-1">
                      {insBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Instrument
                    </button>

                    {(account.instruments || []).length > 0 && (
                      <div className="mt-6 space-y-3" data-testid="biss-ins-list">
                        {account.instruments.map((i) => (
                          <div key={i.id} data-testid={`biss-ins-row-${i.id}`} className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <div className="text-[13px] text-[#F4ECCF] font-semibold">{i.type_name} · {money(i.amount)}</div>
                                <div className="text-[11px] text-[#7fa08f]">No. {i.number} · Payee: {i.payee || "—"}{i.counterparty ? ` · ${i.counterparty}` : ""}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={() => openInstrument(i.id, "print")} data-testid={`biss-ins-print-${i.id}`}
                                  className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-3 py-2 rounded-md"><Printer size={12} /> Print</button>
                                <button onClick={() => openInstrument(i.id, "download")} data-testid={`biss-ins-dl-${i.id}`}
                                  className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-3 py-2 rounded-md"><Download size={12} /> PDF</button>
                                {i.repaid ? (
                                  <span data-testid={`biss-ins-repaid-${i.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6FD3AE] border border-[#2E6F5E]/50 px-3 py-2 rounded-md">Repaid</span>
                                ) : (
                                  <button onClick={() => repayInstrument(i.id)} data-testid={`biss-ins-repay-${i.id}`}
                                    className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#E7F3EE] border border-[#1f4030] hover:border-[#6FD3AE] px-3 py-2 rounded-md"><DollarSign size={12} /> Repay</button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#143026]">
                              <input value={insFax[i.id] || ""} onChange={(e) => setInsFax((p) => ({ ...p, [i.id]: e.target.value }))} placeholder="Counterparty fax +1312..." data-testid={`biss-ins-fax-to-${i.id}`}
                                className="flex-1 max-w-xs bg-[#0C1812] border border-[#1f4030] rounded-md px-3 py-1.5 text-[#E7F3EE] text-[12px]" />
                              <FaxPicker dir={faxDir} testid={`biss-ins-fax-picker-${i.id}`} onPick={(fax) => setInsFax((p) => ({ ...p, [i.id]: fax }))} />
                              <button onClick={() => faxInstrument(i.id)} data-testid={`biss-ins-fax-send-${i.id}`}
                                className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#E7F3EE] border border-[#1f4030] hover:border-[#E4C97A] px-3 py-2 rounded-md"><Send size={12} /> eFax</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {/* Treasury securities — buy/sell via CUSIP reopening */}
                {account && (
                  <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-treasury-card">
                    <h3 className="font-serif text-[18px] text-[#F4ECCF] mb-1">Treasury Securities — Buy / Sell (CUSIP Reopening)</h3>
                    <p className="text-[12px] text-[#7fa08f] mb-4">Transact U.S. Treasury securities directly with the BISS desk by reopening a CUSIP, in lieu of a counterparty deal. Leave CUSIP blank to have the desk assign a reopened CUSIP.</p>
                    <div className="mb-5 bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4" data-testid="biss-auction-desk">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-[0.12em] text-[#E4C97A] font-bold">Treasury Auction Desk — live reopening CUSIPs</div>
                        <button onClick={() => loadAuctions(false)} data-testid="biss-auction-refresh" className="inline-flex items-center gap-1 text-[10.5px] text-[#9fc0af] border border-[#1f4030] hover:border-[#E4C97A] px-2.5 py-1 rounded-md">
                          {auctionsLoading ? <Loader2 size={11} className="animate-spin" /> : <Globe size={11} />} {auctions ? "Refresh" : "Load live data"}
                        </button>
                      </div>
                      {auctions?.source && <div className="text-[9.5px] text-[#5e7a6c] mb-2">Source: {auctions.source} · {auctions.auctions?.length || 0} reopening issues</div>}
                      {auctions?.auctions?.length > 0 && (
                        <div className="max-h-56 overflow-y-auto space-y-1">
                          {auctions.auctions.map((a, i) => (
                            <div key={`${a.cusip}-${i}`} data-testid={`biss-auction-row-${i}`} className="flex items-center justify-between gap-2 text-[11px] border-b border-[#143026] py-1.5">
                              <div className="text-[#cfe3d8]"><b>{a.cusip}</b> · {a.security_type} {a.security_term} · {a.interest_rate ? `${a.interest_rate}%` : `HY ${a.high_yield}`} · auc {a.auction_date} · mat {a.maturity_date}</div>
                              <button onClick={() => applyAuction(a)} data-testid={`biss-auction-use-${i}`} className="shrink-0 text-[10px] font-bold uppercase text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-2.5 py-1 rounded">Use</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">Action</label>
                        <select value={tx.action} onChange={(e) => setT("action")(e.target.value)} data-testid="biss-tx-action" className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]">
                          <option value="buy">Buy (purchase from desk)</option>
                          <option value="sell">Sell (deliver to desk)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">Security</label>
                        <select value={tx.security_type} onChange={(e) => setT("security_type")(e.target.value)} data-testid="biss-tx-security" className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]">
                          {(account.security_types || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      <Field label="CUSIP to reopen (optional)" value={tx.cusip} onChange={setT("cusip")} ph="e.g. 91282CLW9 — blank = desk assigns" testid="biss-tx-cusip" />
                      <Field label="Term" value={tx.term} onChange={setT("term")} ph="e.g. 10-Year" testid="biss-tx-term" />
                      <Field label="Coupon / Rate" value={tx.coupon_rate} onChange={setT("coupon_rate")} ph="e.g. 4.250%" testid="biss-tx-coupon" />
                      <Field label="Yield" value={tx.yield_rate} onChange={setT("yield_rate")} ph="e.g. 4.310%" testid="biss-tx-yield" />
                      <Field label="Par / Face Amount (USD)" value={tx.par_amount} onChange={setT("par_amount")} type="number" ph="e.g. 1000000" testid="biss-tx-par" />
                      <Field label="Price (per 100)" value={tx.price} onChange={setT("price")} type="number" testid="biss-tx-price" />
                      <Field label="Issue / Dated Date" value={tx.issue_date} onChange={setT("issue_date")} type="date" testid="biss-tx-issue" />
                      <Field label="Maturity Date" value={tx.maturity_date} onChange={setT("maturity_date")} type="date" testid="biss-tx-maturity" />
                      <Field label="Settlement Date" value={tx.settlement_date} onChange={setT("settlement_date")} type="date" testid="biss-tx-settle" />
                      <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">Settlement Method</label>
                        <select value={tx.settlement_method} onChange={(e) => setT("settlement_method")(e.target.value)} data-testid="biss-tx-method" className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]">
                          <option value="DVP">DVP (delivery vs payment)</option>
                          <option value="FedWire">FedWire</option>
                          <option value="Free Delivery">Free Delivery</option>
                        </select>
                      </div>
                      <Field label="External Counterparty (optional)" value={tx.counterparty} onChange={setT("counterparty")} testid="biss-tx-counterparty" />
                      <div>
                        <Field label={tx.counterparty.trim() ? "Counterparty Fax (REQUIRED — auto-eFaxed on execution)" : "Counterparty Fax (E.164)"} value={tx.counterparty_fax} onChange={setT("counterparty_fax")} ph="+13125551234" testid="biss-tx-counterparty-fax" />
                        <div className="-mt-2 mb-3"><FaxPicker dir={faxDir} testid="biss-tx-fax-picker" onPick={(fax) => setT("counterparty_fax")(fax)} /></div>
                      </div>
                      <Field label="Counterparty Email (optional)" value={tx.counterparty_email} onChange={setT("counterparty_email")} ph="counterparty@example.com" testid="biss-tx-counterparty-email" />
                    </div>
                    {tx.counterparty.trim() && (
                      <label className="flex items-center gap-2 text-[12px] text-[#9fc0af] mt-2 mb-1 cursor-pointer" data-testid="biss-tx-send-email-row">
                        <input type="checkbox" checked={tx.send_email} onChange={(e) => setT("send_email")(e.target.checked)} data-testid="biss-tx-send-email" />
                        <span className="inline-flex items-center gap-1"><Mail size={12} /> Also email a copy of the executed confirmation to the counterparty (sent alongside the auto-eFax; the firm is copied)</span>
                      </label>
                    )}
                    <Field label="Memo (optional)" value={tx.memo} onChange={setT("memo")} testid="biss-tx-memo" />
                    <div className="grid sm:grid-cols-2 gap-x-6 border-t border-[#143026] pt-4 mt-1">
                      <Field label="Your E-Signature (type full name)" value={tx.esignature_name} onChange={setT("esignature_name")} ph="Type your full legal name" testid="biss-tx-esign-name" />
                    </div>
                    {tx.esignature_name.trim() && (
                      <div className="mb-2"><span style={{ fontFamily: "'Great Vibes', cursive", fontSize: 26, color: "#6FD3AE" }}>{tx.esignature_name}</span></div>
                    )}
                    <label className="flex items-start gap-2 text-[12px] text-[#9fc0af] mb-3 cursor-pointer">
                      <input type="checkbox" checked={tx.esign_attestation} onChange={(e) => setT("esign_attestation")(e.target.checked)} data-testid="biss-tx-esign-attest" className="mt-0.5" />
                      <span>I adopt the typed name as my electronic signature (E-SIGN/UETA) and authorize this Treasury securities transaction by reopening of the CUSIP with the BISS desk, and direct settlement as instructed.</span>
                    </label>
                    <button onClick={createTx} disabled={txBusy} data-testid="biss-tx-create-btn"
                      className="inline-flex items-center gap-2 bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-colors mt-1">
                      {txBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Execute Treasury Transaction
                    </button>

                    {(account.treasury_transactions || []).length > 0 && (
                      <div className="mt-6 space-y-3" data-testid="biss-tx-list">
                        {account.treasury_transactions.map((t) => (
                          <div key={t.id} data-testid={`biss-tx-row-${t.id}`} className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <div className="text-[13px] text-[#F4ECCF] font-semibold">{t.action === "buy" ? "BUY" : "SELL"} · {t.security_name} · {money(t.principal)}
                                  <span className={`ml-2 text-[9px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${t.status === "held" ? "bg-[#143026] text-[#6FD3AE]" : "bg-[#2a1f10] text-[#E4C97A]"}`} data-testid={`biss-tx-status-${t.id}`}>{t.status || "held"}</span>
                                </div>
                                <div className="text-[11px] text-[#7fa08f]">No. {t.number} · CUSIP {t.cusip} · Par {money(t.par_amount)} @ {Number(t.price).toFixed(3)}</div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => openTx(t.id, "print")} data-testid={`biss-tx-print-${t.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-3 py-2 rounded-md"><Printer size={12} /> Print</button>
                                <button onClick={() => openTx(t.id, "download")} data-testid={`biss-tx-dl-${t.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-3 py-2 rounded-md"><Download size={12} /> PDF</button>
                                <button onClick={() => openReceipt(t.id)} data-testid={`biss-tx-receipt-${t.id}`} className="inline-flex items-center gap-1 text-[10.5px] text-[#cfe3d8] border border-[#1f4030] hover:border-[#E4C97A] px-2.5 py-2 rounded-md"><FileText size={12} /> Receipt</button>
                              </div>
                            </div>
                            {t.action === "buy" && (
                              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#143026]">
                                <span className="text-[10px] uppercase tracking-[0.1em] text-[#7fa08f] mr-1">Move holding:</span>
                                <button onClick={() => openMoveFor(t.id, "transfer")} data-testid={`biss-tx-transfer-${t.id}`} className="text-[10.5px] font-bold uppercase text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-3 py-1.5 rounded-md">Transfer / Assign</button>
                                <button onClick={() => openMoveFor(t.id, "loan")} data-testid={`biss-tx-loan-${t.id}`} className="text-[10.5px] font-bold uppercase text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-3 py-1.5 rounded-md">Securities Loan</button>
                                {(t.movements || []).map((mvdoc) => (
                                  <button key={mvdoc.id} onClick={() => openMovementDoc(t.id, mvdoc.id)} data-testid={`biss-tx-mvdoc-${mvdoc.id}`} className="inline-flex items-center gap-1 text-[10px] text-[#9fc0af] border border-[#1f4030] hover:border-[#E4C97A] px-2.5 py-1.5 rounded-md"><FileText size={11} /> {mvdoc.number}</button>
                                ))}
                              </div>
                            )}
                            {moveFor === t.id && (
                              <div className="mt-3 pt-3 border-t border-[#143026] bg-[#0b140f] rounded-lg p-3" data-testid={`biss-move-form-${t.id}`}>
                                <div className="text-[11px] text-[#E4C97A] font-bold mb-2">{moveForm.mode === "loan" ? "Securities Loan (standard market practice)" : "Transfer & Assignment to a third party"}</div>
                                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
                                  <Field label={moveForm.mode === "loan" ? "Borrower" : "Assignee (third party)"} value={moveForm.third_party} onChange={setMv("third_party")} testid="biss-move-thirdparty" />
                                  <Field label="Third party address" value={moveForm.third_party_address} onChange={setMv("third_party_address")} testid="biss-move-address" />
                                  {moveForm.mode === "loan" && <Field label="Collateral %" value={moveForm.collateral_pct} onChange={setMv("collateral_pct")} type="number" testid="biss-move-collateral" />}
                                  {moveForm.mode === "loan" && <Field label="Rebate / Fee rate" value={moveForm.rebate_rate} onChange={setMv("rebate_rate")} ph="e.g. 4.80%" testid="biss-move-rebate" />}
                                  {moveForm.mode === "loan" && <Field label="Term" value={moveForm.term} onChange={setMv("term")} ph="Open or 30 days" testid="biss-move-term" />}
                                  <Field label="Your E-Signature" value={moveForm.esignature_name} onChange={setMv("esignature_name")} ph="Type your full legal name" testid="biss-move-esign" />
                                </div>
                                <label className="flex items-start gap-2 text-[11px] text-[#9fc0af] my-2 cursor-pointer">
                                  <input type="checkbox" checked={moveForm.esign_attestation} onChange={(e) => setMv("esign_attestation")(e.target.checked)} data-testid="biss-move-attest" className="mt-0.5" />
                                  <span>I e-sign (E-SIGN/UETA) and authorize this {moveForm.mode === "loan" ? "securities loan" : "transfer and assignment"} of the securities to the named third party.</span>
                                </label>
                                <div className="flex gap-2">
                                  <button onClick={submitMove} data-testid="biss-move-submit" className="text-[10.5px] font-bold uppercase text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-4 py-2 rounded-md">Execute & Generate Doc</button>
                                  <button onClick={() => setMoveFor(null)} data-testid="biss-move-cancel" className="text-[10.5px] text-[#7fa08f] hover:text-[#E7F3EE] px-3 py-2">Cancel</button>
                                </div>
                              </div>
                            )}
                            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-[#143026]">
                              <div className="text-[10px] uppercase tracking-[0.1em] text-[#7fa08f]">Transmit this document</div>
                              <div className="flex flex-wrap items-center gap-2">
                                <input value={txEmail[t.id] || ""} onChange={(e) => setTxEmail((p) => ({ ...p, [t.id]: e.target.value }))} placeholder="Recipient email" type="email" data-testid={`biss-tx-email-to-${t.id}`} className="flex-1 min-w-[180px] max-w-xs bg-[#0C1812] border border-[#1f4030] rounded-md px-3 py-1.5 text-[#E7F3EE] text-[12px]" />
                                <button onClick={() => emailTx(t.id)} data-testid={`biss-tx-email-send-${t.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-3 py-2 rounded-md"><Send size={12} /> Email</button>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <input value={txFax[t.id] || ""} onChange={(e) => setTxFax((p) => ({ ...p, [t.id]: e.target.value }))} placeholder="Counterparty fax +1312..." data-testid={`biss-tx-fax-to-${t.id}`} className="flex-1 min-w-[180px] max-w-xs bg-[#0C1812] border border-[#1f4030] rounded-md px-3 py-1.5 text-[#E7F3EE] text-[12px]" />
                                <FaxPicker dir={faxDir} testid={`biss-tx-fax-picker-${t.id}`} onPick={(fax) => setTxFax((p) => ({ ...p, [t.id]: fax }))} />
                                <button onClick={() => faxTx(t.id)} data-testid={`biss-tx-fax-send-${t.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#E7F3EE] border border-[#1f4030] hover:border-[#E4C97A] px-3 py-2 rounded-md"><Send size={12} /> eFax</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {/* BISS $30B Treasury Securities Reserve — securities loans */}
                {account && (
                  <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-reserve-card">
                    <h3 className="font-serif text-[18px] text-[#F4ECCF] mb-1">Treasury Securities Reserve — Securities Loan</h3>
                    <p className="text-[12px] text-[#7fa08f] mb-4">Draw a securities loan from the Association's $30,000,000,000 U.S. Treasury securities reserve. A facility fee of <b>0.5%</b> of the total purchase price applies for each <b>90-day</b> term.</p>
                    <div className="grid sm:grid-cols-3 gap-3 mb-5">
                      <div className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#8fae9f]">Reserve (aggregate)</div>
                        <div className="text-[16px] text-[#F4ECCF] font-semibold" data-testid="biss-reserve-total">{reserve ? money(reserve.total) : "$30,000,000,000"}</div>
                      </div>
                      <div className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#8fae9f]">Available</div>
                        <div className="text-[16px] text-[#6FD3AE] font-semibold" data-testid="biss-reserve-available">{reserve ? money(reserve.available) : "—"}</div>
                      </div>
                      <div className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#8fae9f]">Fee · Term</div>
                        <div className="text-[16px] text-[#E4C97A] font-semibold">0.5% · 90 days</div>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      <Field label="Total Purchase Price (USD)" value={rsl.purchase_price} onChange={setR("purchase_price")} type="number" ph="e.g. 10000000" testid="biss-reserve-price" />
                      <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">Security</label>
                        <select value={rsl.security_type} onChange={(e) => setR("security_type")(e.target.value)} data-testid="biss-reserve-security" className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]">
                          {(account.security_types || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      <Field label="CUSIP (optional)" value={rsl.cusip} onChange={setR("cusip")} ph="e.g. 91282CLW9" testid="biss-reserve-cusip" />
                      <Field label="Memo (optional)" value={rsl.memo} onChange={setR("memo")} testid="biss-reserve-memo" />
                    </div>
                    <div className="mt-3 bg-[#0F1A14] border border-[#1f4030] rounded-lg px-4 py-3 text-[12px] text-[#cfe3d8]" data-testid="biss-reserve-fee-preview">
                      Facility fee (0.5% × purchase price, per 90-day term): <b className="text-[#E4C97A]">{money((Number(rsl.purchase_price) || 0) * 0.005)}</b>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 border-t border-[#143026] pt-4 mt-3">
                      <Field label="Your E-Signature (type full name)" value={rsl.esignature_name} onChange={setR("esignature_name")} ph="Type your full legal name" testid="biss-reserve-esign-name" />
                    </div>
                    {rsl.esignature_name.trim() && (
                      <div className="mb-2"><span style={{ fontFamily: "'Great Vibes', cursive", fontSize: 26, color: "#6FD3AE" }}>{rsl.esignature_name}</span></div>
                    )}
                    <label className="flex items-start gap-2 text-[12px] text-[#9fc0af] mb-3 cursor-pointer">
                      <input type="checkbox" checked={rsl.esign_attestation} onChange={(e) => setR("esign_attestation")(e.target.checked)} data-testid="biss-reserve-esign-attest" className="mt-0.5" />
                      <span>I adopt the typed name as my electronic signature (E-SIGN/UETA) and borrow the above securities from the BISS Treasury reserve, agreeing to the 0.5% facility fee per 90-day term.</span>
                    </label>
                    <button onClick={createReserveLoan} disabled={rslBusy} data-testid="biss-reserve-create-btn"
                      className="inline-flex items-center gap-2 bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-colors mt-1">
                      {rslBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Execute Securities Loan
                    </button>

                    {reserve && (reserve.loans || []).length > 0 && (
                      <div className="mt-6 space-y-3" data-testid="biss-reserve-list">
                        {reserve.loans.map((l) => (
                          <div key={l.id} data-testid={`biss-reserve-row-${l.id}`} className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <div className="text-[13px] text-[#F4ECCF] font-semibold">{l.security_name} · {money(l.purchase_price)}
                                  <span className={`ml-2 text-[9px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${l.status === "active" ? "bg-[#143026] text-[#6FD3AE]" : "bg-[#2a1f10] text-[#E4C97A]"}`} data-testid={`biss-reserve-status-${l.id}`}>{l.status}</span>
                                  <span className={`ml-2 text-[9px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${l.fee_status === "invoiced" ? "bg-[#2a1f10] text-[#E4C97A]" : "bg-[#143026] text-[#6FD3AE]"}`} data-testid={`biss-reserve-feestatus-${l.id}`}>fee {(l.fee_status || "invoiced").replace("_", " ")}</span>
                                </div>
                                <div className="text-[11px] text-[#7fa08f]">No. {l.number} · fee {money(l.total_fees)} ({l.periods}× term) · matures {l.maturity_date}</div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => openReserveAgreement(l.id)} data-testid={`biss-reserve-doc-${l.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-3 py-2 rounded-md"><FileText size={12} /> Agreement</button>
                                {l.status === "active" && <button onClick={() => renewReserve(l.id)} data-testid={`biss-reserve-renew-${l.id}`} className="text-[10.5px] font-bold uppercase text-[#cfe3d8] border border-[#1f4030] hover:border-[#E4C97A] px-3 py-2 rounded-md">Renew +90d</button>}
                                {l.status === "active" && <button onClick={() => returnReserve(l.id)} data-testid={`biss-reserve-return-${l.id}`} className="text-[10.5px] font-bold uppercase text-[#cfe3d8] border border-[#1f4030] hover:border-[#E4C97A] px-3 py-2 rounded-md">Return</button>}
                              </div>
                            </div>
                            {(l.fee_status === "invoiced" || l.fee_status === "pending") && (
                              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#143026]">
                                <span className="text-[10px] uppercase tracking-[0.1em] text-[#7fa08f] mr-1">Settle 0.5% fee:</span>
                                <button onClick={() => payReserveStripe(l.id)} data-testid={`biss-reserve-pay-${l.id}`} className="text-[10.5px] font-bold uppercase text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-3 py-1.5 rounded-md">Pay by card</button>
                                <button onClick={() => settleReserveCredit(l.id)} data-testid={`biss-reserve-settle-credit-${l.id}`} className="text-[10.5px] font-bold uppercase text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-3 py-1.5 rounded-md">Deduct from credit line</button>
                              </div>
                            )}
                            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-[#143026]">
                              <div className="text-[10px] uppercase tracking-[0.1em] text-[#7fa08f]">Transmit agreement</div>
                              <div className="flex flex-wrap items-center gap-2">
                                <input value={rslEmail[l.id] || ""} onChange={(e) => setRslEmail((p) => ({ ...p, [l.id]: e.target.value }))} placeholder="Recipient email" type="email" data-testid={`biss-reserve-email-to-${l.id}`} className="flex-1 min-w-[180px] max-w-xs bg-[#0C1812] border border-[#1f4030] rounded-md px-3 py-1.5 text-[#E7F3EE] text-[12px]" />
                                <button onClick={() => emailReserve(l.id)} data-testid={`biss-reserve-email-send-${l.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-3 py-2 rounded-md"><Send size={12} /> Email</button>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <input value={rslFax[l.id] || ""} onChange={(e) => setRslFax((p) => ({ ...p, [l.id]: e.target.value }))} placeholder="Counterparty fax +1312..." data-testid={`biss-reserve-fax-to-${l.id}`} className="flex-1 min-w-[180px] max-w-xs bg-[#0C1812] border border-[#1f4030] rounded-md px-3 py-1.5 text-[#E7F3EE] text-[12px]" />
                                <FaxPicker dir={faxDir} testid={`biss-reserve-fax-picker-${l.id}`} onPick={(fax) => setRslFax((p) => ({ ...p, [l.id]: fax }))} />
                                <button onClick={() => faxReserve(l.id)} data-testid={`biss-reserve-fax-send-${l.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#E7F3EE] border border-[#1f4030] hover:border-[#E4C97A] px-3 py-2 rounded-md"><Send size={12} /> eFax</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}


                {/* Issue / print / download */}
                <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-issue-card">
                  <h3 className="font-serif text-[18px] text-[#F4ECCF] mb-1">Your Master Charter Package</h3>
                  <p className="text-[12px] text-[#7fa08f] mb-4">
                    Master Charter + Articles of Association & International Banking By-Laws + Collateral Pledge +
                    Treasury Authorization + Private Banker Certificate of Authority.
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <button onClick={() => openDoc("full", "print")} data-testid="biss-print-full"
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-4 py-2.5 rounded-md transition-colors">
                      <Printer size={14} /> Print Full Package
                    </button>
                    <button onClick={() => openDoc("full", "download")} data-testid="biss-download-full"
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-4 py-2.5 rounded-md transition-colors">
                      <Download size={14} /> Download Full Package
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#143026]">
                    <span className="text-[10.5px] uppercase tracking-[0.12em] text-[#7fa08f] mr-1">Individual documents:</span>
                    {DOC_OPTIONS.filter((d) => d.id !== "full").map((d) => (
                      <div key={d.id} className="inline-flex rounded-md overflow-hidden border border-[#1f4030]">
                        <button onClick={() => openDoc(d.id, "view")} data-testid={`biss-doc-view-${d.id}`}
                          className="inline-flex items-center gap-1 text-[10.5px] text-[#cfe3d8] hover:bg-[#12271d] px-2.5 py-1.5 transition-colors">
                          {d.name}
                        </button>
                        <button onClick={() => openDoc(d.id, "download")} data-testid={`biss-doc-dl-${d.id}`}
                          className="inline-flex items-center text-[#9fc0af] hover:bg-[#12271d] px-2 py-1.5 border-l border-[#1f4030] transition-colors" title="Download">
                          <Download size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>

                {/* eFax to counterparty */}
                <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-efax-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Send size={15} className="text-[#E4C97A]" />
                    <h3 className="font-serif text-[18px] text-[#F4ECCF]">Transmit to a Counterparty (eFax)</h3>
                  </div>
                  <p className="text-[12px] text-[#7fa08f] mb-4">Fax your back-office instrument directly to a counterparty's fax line.</p>
                  <div className="grid sm:grid-cols-2 gap-x-6">
                    <Field label="Counterparty Name (optional)" value={faxCounterparty} onChange={setFaxCounterparty} ph="e.g. Correspondent Bank Ltd." testid="biss-fax-counterparty" />
                    <Field label="Counterparty Fax (E.164)" value={faxTo} onChange={setFaxTo} ph="+13125551234" testid="biss-fax-to" />
                  </div>
                  <div className="mb-3">
                    <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">Document to send</label>
                    <select value={faxDoc} onChange={(e) => setFaxDoc(e.target.value)} data-testid="biss-fax-doc"
                      className="w-full sm:w-80 bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]">
                      {DOC_OPTIONS.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <button onClick={sendFax} disabled={faxing} data-testid="biss-fax-send"
                    className="inline-flex items-center gap-2 bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-colors">
                    {faxing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send eFax
                  </button>
                </section>

                {/* Transaction Records */}
                <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="biss-txn-records-card">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Receipt size={15} className="text-[#E4C97A]" />
                      <h3 className="font-serif text-[18px] text-[#F4ECCF]">Transaction Records</h3>
                    </div>
                    <button onClick={() => loadTxns(me.id, token)} data-testid="biss-txn-refresh"
                      className="text-[10px] uppercase tracking-[0.08em] text-[#7fa08f] hover:text-[#E4C97A]">Refresh</button>
                  </div>
                  <p className="text-[12px] text-[#7fa08f] mb-4">A full database record of every transaction you execute — instruments, Treasury trades, transfers, securities loans, and eFax transmissions. The Association mirrors this record for oversight.</p>
                  {txns.length === 0 ? (
                    <div className="text-[12px] text-[#7fa08f] py-4 text-center">No transactions yet.</div>
                  ) : (
                    <div className="overflow-x-auto" data-testid="biss-txn-table">
                      <table className="w-full text-[11.5px]">
                        <thead><tr className="text-[#7fa08f] border-b border-[#143026] text-left">
                          <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Reference</th>
                          <th className="py-2 pr-3">Counterparty</th><th className="py-2 pr-3 text-right">Amount</th><th className="py-2 pr-3">eFax</th>
                        </tr></thead>
                        <tbody>
                          {txns.map((r) => (
                            <tr key={r.id} className="border-b border-[#0f251a] last:border-0" data-testid={`biss-txn-row-${r.id}`}>
                              <td className="py-2 pr-3 text-[#9fc0af]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                              <td className="py-2 pr-3 text-[#cfe3d8] capitalize">{String(r.kind || "").replace(/_/g, " ")}</td>
                              <td className="py-2 pr-3 text-[#9fc0af]">{r.number || "—"}</td>
                              <td className="py-2 pr-3 text-[#9fc0af]">{r.counterparty || "—"}</td>
                              <td className="py-2 pr-3 text-right text-[#E4C97A]">{money(r.amount)}</td>
                              <td className="py-2 pr-3">{r.fax && r.fax.attempted ? (
                                <span className={r.fax.status === "failed" ? "text-[#E89A9A]" : "text-[#6FD3AE]"}>{r.fax.status}{r.fax.to ? ` → ${r.fax.to}` : ""}</span>
                              ) : <span className="text-[#5e7a6c]">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </>
            )}
          </>
        )}

        <p className="text-center text-[11px] text-[#5e7a6c] leading-relaxed pb-8 max-w-3xl mx-auto">
          Private membership association constituted under private treaty and contract. The Master Charter is a private
          membership record and does not constitute a government charter, banking license, or authorization to act as a
          bank. The <strong>Bank of International Settlements and Securities</strong> is a private commercial institution;
          it is <strong>not a government agency or central bank</strong> and is <strong>not affiliated with, endorsed by,
          or connected to</strong> the United States Government, the U.S. Department of the Treasury, the Internal Revenue
          Service, the Federal Reserve System, or the Bank for International Settlements (BIS, Basel). Instruments issued
          here are private commercial obligations; they are not government instruments and are not FDIC, NCUA, or SIPC
          insured. All services are subject to the private association agreement.
        </p>
      </main>
    </div>
  );
}
